/**
 * BriefingExportActions
 *
 * Wiederverwendbare Toolbar-Aktionen für ein Mission-Briefing:
 *  - Briefing-Text in die Zwischenablage kopieren (navigator.clipboard.writeText)
 *  - Briefing als Markdown-Datei (.md) herunterladen
 *
 * Operiert immer auf dem übergebenen `text` (= aktuell angezeigter Body), gilt
 * daher gleichermaßen für KI-generierte wie manuell editierte Briefings.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../toast.js';

// Stil der bestehenden read-mode-Toolbar-Buttons in briefing-view.tsx.
const toolbarButtonClass = 'text-xs px-2 py-1 rounded';
const toolbarButtonStyle = {
  color: 'var(--primary)',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: 'var(--f-mono)',
} as const;

/**
 * Macht einen String dateinamen-sicher: deutsche Umlaute/ß werden transliteriert
 * (statt verschluckt: `Müller` → `Mueller`, nicht `Mller`), Whitespace → `_`,
 * alles außerhalb `[A-Za-z0-9._-]` wird entfernt.
 */
function sanitizeFileNamePart(value: string): string {
  return value
    .trim()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae')
    .replace(/Ö/g, 'Oe')
    .replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9._-]/g, '');
}

/**
 * Baut einen sprechenden Dateinamen `Mission-Briefing_<projectKey>_<Kunde>.md`.
 * Beide Teile werden über {@link sanitizeFileNamePart} dateinamen-sicher gemacht
 * (auch der Key — die Komponente ist wiederverwendbar und darf keine rohen
 * Pfadzeichen durchreichen); ein leeres Ergebnis fällt auf `Briefing` zurück.
 */
export function buildBriefingFileName(projectKey: string, customerName: string): string {
  const keyPart = sanitizeFileNamePart(projectKey) || 'Briefing';
  const customerPart = sanitizeFileNamePart(customerName) || 'Briefing';
  return `Mission-Briefing_${keyPart}_${customerPart}.md`;
}

export function BriefingExportActions({
  text,
  fileName,
}: {
  text: string;
  fileName: string;
}): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const copyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyTimeout.current !== null) clearTimeout(copyTimeout.current);
    },
    [],
  );

  async function handleCopy() {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error(t('v2.briefing_view.copy_unsupported', 'Zwischenablage nicht verfügbar'));
      }
      await navigator.clipboard.writeText(text);
      toast.success(t('v2.briefing_view.copy_success', 'In Zwischenablage kopiert'));
      setCopied(true);
      if (copyTimeout.current !== null) clearTimeout(copyTimeout.current);
      copyTimeout.current = setTimeout(() => {
        setCopied(false);
        copyTimeout.current = null;
      }, 1500);
    } catch (err: unknown) {
      const detail = err instanceof Error ? err.message : String(err);
      toast.error(t('v2.briefing_view.copy_failed', 'Kopieren fehlgeschlagen'), detail);
    }
  }

  function handleDownload() {
    if (typeof document === 'undefined') return;
    let url: string | null = null;
    try {
      const blob = new Blob([text], { type: 'text/markdown' });
      url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(t('v2.briefing_view.download_started', 'Markdown-Download gestartet'));
    } catch (err: unknown) {
      const detail = err instanceof Error ? err.message : String(err);
      toast.error(t('v2.briefing_view.download_failed', 'Download fehlgeschlagen'), detail);
    } finally {
      // Revoke erst nach dem aktuellen Tick — sonst kann der Browser den Blob
      // u. U. noch nicht gelesen haben und der Download schlägt fehl.
      if (url !== null) {
        const toRevoke = url;
        setTimeout(() => URL.revokeObjectURL(toRevoke), 0);
      }
    }
  }

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={handleCopy}
        className={toolbarButtonClass}
        style={toolbarButtonStyle}
        aria-label={t('v2.briefing_view.copy_aria', 'Briefing-Text in Zwischenablage kopieren')}
      >
        {copied
          ? `✓ ${t('v2.briefing_view.copied', 'Kopiert')}`
          : `⧉ ${t('v2.briefing_view.copy', 'Kopieren')}`}
      </button>
      <button
        type="button"
        onClick={handleDownload}
        className={toolbarButtonClass}
        style={toolbarButtonStyle}
        aria-label={t(
          'v2.briefing_view.download_aria',
          'Briefing als Markdown-Datei herunterladen',
        )}
      >
        ↓ {t('v2.briefing_view.download_md', 'Als Markdown')}
      </button>
    </div>
  );
}
